import { http } from "../../services/http";

export const API_RESOURCE = "forms";

/* ----------------------------------------------------------------- IDs ---- */
export function genId(prefix = "f") {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

/* Tipos de campo sem valor (não participam de lógica/cálculo/escore no builder). */
export const LAYOUT_FIELD_TYPES = ["section", "paragraph"];

/* Substitui o item em `idx` por `{ ...item, ...patch }`, mantendo os demais. */
export function updateAt(list, idx, patch) {
  return list.map((item, i) => (i === idx ? { ...item, ...patch } : item));
}

/* ------------------------------------------------------------- Datas ---- */
export function fmtDateTime(v) {
  if (!v) return "-";
  const d = new Date(v);
  return isNaN(d) ? "-" : d.toLocaleString("pt-BR");
}

/* Cores de faixa de risco clínico, usadas nos chips de escore e no PDF exportado. */
export const RISK_COLOR = {
  "Muito Baixo": "#2e7d32", Baixo: "#66bb6a", Nenhum: "#9e9e9e",
  Intermediário: "#f9a825", Alto: "#ef6c00", "Muito Alto": "#c62828",
};

/* ----------------------------------------------- Registry de tipos de campo */
// category: "entrada" | "escolha" | "layout"
export const FIELD_TYPES = [
  { type: "text",      label: "Texto curto",     icon: "fa fa-font",          category: "entrada" },
  { type: "textarea",  label: "Texto longo",     icon: "fa fa-align-left",    category: "entrada" },
  { type: "number",    label: "Número",          icon: "fa fa-hashtag",       category: "entrada" },
  { type: "email",     label: "E-mail",          icon: "fa fa-envelope",      category: "entrada" },
  { type: "phone",     label: "Telefone",        icon: "fa fa-phone",         category: "entrada" },
  { type: "date",      label: "Data",            icon: "fa fa-calendar",      category: "entrada" },
  { type: "radio",     label: "Escolha única",   icon: "fa fa-dot-circle-o",  category: "escolha", hasOptions: true },
  { type: "checkbox",  label: "Múltipla escolha", icon: "fa fa-check-square-o", category: "escolha", hasOptions: true, multi: true },
  { type: "select",    label: "Lista suspensa",  icon: "fa fa-caret-square-o-down", category: "escolha", hasOptions: true },
  { type: "scale",     label: "Escala (0-10)",   icon: "fa fa-sliders",       category: "escolha" },
  { type: "yesno",     label: "Sim / Não",       icon: "fa fa-toggle-on",     category: "escolha", hasOptions: true },
  { type: "section",   label: "Seção / Título",  icon: "fa fa-header",        category: "layout" },
  { type: "paragraph", label: "Texto explicativo", icon: "fa fa-paragraph",   category: "layout" },
  { type: "cover",     label: "Capa / Boas-vindas", icon: "fa fa-flag",       category: "layout" },
  { type: "consent",   label: "Termos e Condições", icon: "fa fa-check-square-o", category: "layout" },
];

export const FIELD_TYPE_MAP = Object.fromEntries(FIELD_TYPES.map((f) => [f.type, f]));

// Derivado dos nomes já existentes, não de um contador de módulo: o contador zerava a cada
// reload e dois campos ficavam com o mesmo `name` — buildContext resolveria só o último.
function defaultName(type, existingFields) {
  const taken = new Set((existingFields || []).map((f) => f && f.name).filter(Boolean));
  let n = 1;
  while (taken.has(`${type}_${n}`)) n += 1;
  return `${type}_${n}`;
}

export function newField(type, existingFields = []) {
  const meta = FIELD_TYPE_MAP[type] || {};
  const id = genId("fld");
  const base = {
    id,
    type,
    name: defaultName(type, existingFields),
    label: meta.label || "Campo",
    required: false,
    placeholder: "",
    helpText: "",
    visibleIf: null,
    validation: {},
  };
  if (meta.hasOptions) {
    if (type === "yesno") {
      base.options = [
        { value: "sim", label: "Sim" },
        { value: "nao", label: "Não" },
      ];
    } else {
      base.options = [
        { value: "opcao_1", label: "Opção 1" },
        { value: "opcao_2", label: "Opção 2" },
      ];
    }
  }
  if (type === "section") base.label = "Nova seção";
  if (type === "paragraph") base.label = "Texto de apoio para o respondente.";
  if (type === "cover") {
    base.label = "Bem-vindo";
    base.body = "Escreva aqui a mensagem de boas-vindas ao respondente.";
    base.buttonLabel = "Começar";
  }
  if (type === "consent") {
    base.label = "Termos e Condições";
    base.body = "Cole aqui o texto completo dos termos e condições.";
    base.checkboxLabel = "Concordo com os termos e condições acima";
    base.required = true;
  }
  return base;
}

/* ------------------------------------------------- Referências a um campo/var */
// Identificadores inteiros da expressão: `peso` não pode casar com `peso_total`.
function exprRefs(expr) {
  return String(expr === undefined || expr === null ? "" : expr).match(/[A-Za-z_][A-Za-z0-9_]*/g) || [];
}

function conditionRefs(cond) {
  if (!cond || typeof cond !== "object") return [];
  if (Array.isArray(cond.rules)) return cond.rules.reduce((acc, r) => acc.concat(conditionRefs(r)), []);
  return cond.field === undefined || cond.field === null ? [] : [cond.field];
}

/**
 * Onde a definição referencia `refs` (id ou nome de campo, nome de derivação).
 * Comparação estrutural — nada de substring sobre o JSON. Retorna rótulos legíveis.
 */
export function findReferences(definition, refs) {
  const wanted = new Set((refs || []).filter(Boolean).map(String));
  const hits = (list) => (list || []).some((v) => v !== undefined && v !== null && wanted.has(String(v)));
  const def = definition || {};
  const out = [];

  (def.logic || []).forEach((rule, i) => {
    // skipTo aponta para página, não para campo
    const targets = (rule.actions || []).filter((a) => a.type !== "skipTo").map((a) => a.target);
    if (hits(conditionRefs(rule.when)) || hits(targets)) out.push(`Lógica: regra ${i + 1}`);
  });
  (def.calculations || []).forEach((c) => {
    if (hits(exprRefs(c.expression)) || hits([c.target])) out.push(`Cálculo: ${c.name || c.id}`);
  });
  (def.fields || []).forEach((f) => {
    if (hits(conditionRefs(f.visibleIf))) out.push(`Visibilidade: ${f.label || f.name || f.id}`);
  });

  const scoring = def.scoring || {};
  (scoring.derivations || []).forEach((d) => {
    const rowRefs = (d.rows || []).reduce((acc, r) => acc.concat(conditionRefs(r.when)), []);
    if (hits([d.input, d.source]) || hits(d.sources) || hits(exprRefs(d.expr)) || hits(rowRefs)) {
      out.push(`Escore: ${d.name || d.id}`);
    }
  });
  const output = scoring.output || {};
  if (hits(output.headline) || hits(output.lists) || hits((output.items || []).map((it) => it.var))) {
    out.push("Escore: saída");
  }
  return out;
}

// Grupo que ficaria sem regras vira null: `{op, rules: []}` é sempre verdadeiro.
function stripCondition(cond, wanted) {
  if (!cond || typeof cond !== "object") return cond || null;
  if (Array.isArray(cond.rules)) {
    const rules = cond.rules.map((r) => stripCondition(r, wanted)).filter(Boolean);
    return rules.length ? { ...cond, rules } : null;
  }
  return wanted.has(String(cond.field)) ? null : cond;
}

/**
 * Remove as regras órfãs que citam `refs`. Regra cuja condição sumiria por inteiro é
 * descartada: sem condição ela passaria a valer sempre (`is_empty(undefined)` é true).
 * Expressões de cálculo/derivação não são reescritas — resolvem para o `else`/0, e
 * reescrever texto de fórmula é o que traria de volta o casamento por substring.
 */
export function removeReferences(definition, refs) {
  const wanted = new Set((refs || []).filter(Boolean).map(String));
  const def = definition || {};
  const next = { ...def };

  if (def.logic) {
    next.logic = def.logic
      .map((rule) => ({
        ...rule,
        when: stripCondition(rule.when, wanted),
        actions: (rule.actions || []).filter((a) => a.type === "skipTo" || !wanted.has(String(a.target))),
      }))
      .filter((rule, i) => {
        if (def.logic[i].when && !rule.when) return false;
        return rule.actions.length > 0;
      });
  }
  if (def.fields) {
    next.fields = def.fields.map((f) => (f.visibleIf ? { ...f, visibleIf: stripCondition(f.visibleIf, wanted) } : f));
  }
  if (def.scoring && def.scoring.derivations) {
    next.scoring = {
      ...def.scoring,
      derivations: def.scoring.derivations.map((d) => {
        const patched = { ...d };
        if (d.sources) patched.sources = d.sources.filter((s) => !wanted.has(String(s)));
        if (d.rows) {
          patched.rows = d.rows
            .map((r) => ({ ...r, when: stripCondition(r.when, wanted) }))
            .filter((r, i) => !(d.rows[i].when && !r.when));
        }
        return patched;
      }),
    };
  }
  return next;
}

/**
 * Modelo de escore APA pronto (a antiga lógica cravada, agora como DADOS).
 * As `sources`/`input` referenciam NOMES de campos — ajuste no editor para
 * apontar aos campos do seu formulário (idade, peso, altura, cardiac, ...).
 */
export function apaSeedScoring() {
  return {
    enabled: true,
    derivations: [
      { id: genId("d"), name: "imc", type: "expression", expr: "peso / (altura*altura)" },
      { id: genId("d"), name: "imc_risco", type: "range", input: "imc",
        bands: [{ min: 30, max: "", output: 3 }], else: 1 },
      { id: genId("d"), name: "imc_classe", type: "range", input: "imc", bands: [
        { min: "", max: 18.5, output: "Baixo peso" },
        { min: 18.5, max: 25, output: "Peso normal" },
        { min: 25, max: 30, output: "Sobrepeso" },
        { min: 30, max: 35, output: "Obesidade I" },
        { min: 35, max: 40, output: "Obesidade II" },
        { min: 40, max: "", output: "Obesidade III" },
      ], else: "—" },
      { id: genId("d"), name: "idade_risco", type: "range", input: "idade",
        bands: [{ min: 70, max: "", output: 3 }], else: 1 },
      { id: genId("d"), name: "cardio_max", type: "aggregate", op: "max", sources: ["cardiac"], attribute: "score" },
      { id: genId("d"), name: "pulmonar_max", type: "aggregate", op: "max", sources: ["pulmonary"], attribute: "score" },
      { id: genId("d"), name: "endocrino_max", type: "aggregate", op: "max", sources: ["endocrine"], attribute: "score" },
      { id: genId("d"), name: "outras_max", type: "aggregate", op: "max", sources: ["others"], attribute: "score" },
      { id: genId("d"), name: "risco_num", type: "expression",
        expr: "max(idade_risco, imc_risco, cardio_max, pulmonar_max, endocrino_max, outras_max)" },
      { id: genId("d"), name: "risco_paciente", type: "map", input: "risco_num", pairs: [
        { when: "1", then: "Muito Baixo" }, { when: "2", then: "Baixo" },
        { when: "3", then: "Intermediário" }, { when: "4", then: "Alto" },
        { when: "5", then: "Muito Alto" },
      ], else: "Muito Baixo" },
      { id: genId("d"), name: "asa", type: "decision", strategy: "max", rows: [
        { when: { op: "and", rules: [{ field: "risco_num", operator: "gte", value: "4" }] }, output: 3 },
        { when: { op: "and", rules: [{ field: "risco_num", operator: "gte", value: "2" }] }, output: 2 },
      ], else: 1 },
      { id: genId("d"), name: "medicamentos", type: "collect", source: "medications", attribute: "suspensionDays" },
    ],
    output: {
      headline: ["risco_paciente", "asa"],
      items: [
        { label: "IMC", var: "imc" },
        { label: "Classificação IMC", var: "imc_classe" },
      ],
      lists: ["medicamentos"],
    },
  };
}

export function emptyDefinition() {
  return {
    pages: [{ id: "page_1", title: "Página 1", fieldIds: [] }],
    fields: [],
    logic: [],
    calculations: [],
    theme: {},
  };
}

/* ------------------------------------------------------------- API client -- */
export const FormsApi = {
  list: (queryString = "") => http.get(`${API_RESOURCE}${queryString}`),
  get: (id) => http.get(`${API_RESOURCE}/${id}`),
  create: (data) => http.post(`${API_RESOURCE}`, data),
  update: (id, data) => http.put(`${API_RESOURCE}/${id}`, data),
  remove: (id) => http.delete(`${API_RESOURCE}/${id}`),
  publish: (id) => http.post(`${API_RESOURCE}/${id}/publish`),
  saveDraft: (id, definition) => http.put(`${API_RESOURCE}/${id}/draft`, { definition }),
  importJotform: (data) => http.post(`${API_RESOURCE}/import/jotform`, data),

  listInvitations: (id, queryString = "") => http.get(`${API_RESOURCE}/${id}/invitations${queryString}`),
  createInvitations: (id, payload) => http.post(`${API_RESOURCE}/${id}/invitations`, payload),
  sendInvitation: (id, invitationId, payload = {}) => http.post(`${API_RESOURCE}/${id}/invitations/${invitationId}/send`, payload),
  deleteInvitation: (id, invitationId) => http.delete(`${API_RESOURCE}/${id}/invitations/${invitationId}`),

  listResponses: (id, queryString = "") => http.get(`${API_RESOURCE}/${id}/responses${queryString}`),
  getResponse: (id, responseId) => http.get(`${API_RESOURCE}/${id}/responses/${responseId}`),
  getInsumo: (id, responseId) => http.get(`${API_RESOURCE}/${id}/responses/${responseId}/insumo`),

  // Dashboard
  getDashboard: (queryString = "") => http.get(`${API_RESOURCE}/dashboard/funnel${queryString}`),
  getDashboardList: (queryString = "") => http.get(`${API_RESOURCE}/dashboard/funnel/list${queryString}`),
  getInteressados: (queryString = "") => http.get(`${API_RESOURCE}/dashboard/interessados${queryString}`),

  // WhatsApp: configuração + templates
  getWhatsappSettings: () => http.get(`${API_RESOURCE}/whatsapp/settings`),
  saveWhatsappSettings: (data) => http.put(`${API_RESOURCE}/whatsapp/settings`, data),
  listTemplates: () => http.get(`${API_RESOURCE}/whatsapp/templates`),
  listTwilioTemplates: () => http.get(`${API_RESOURCE}/whatsapp/twilio-templates`),
  createTemplate: (data) => http.post(`${API_RESOURCE}/whatsapp/templates`, data),
  updateTemplate: (templateId, data) => http.put(`${API_RESOURCE}/whatsapp/templates/${templateId}`, data),
  deleteTemplate: (templateId) => http.delete(`${API_RESOURCE}/whatsapp/templates/${templateId}`),
};

export const CAMPAIGN_RESOURCE = "form-campaigns";

export const CampaignsApi = {
  list: (queryString = "") => http.get(`${CAMPAIGN_RESOURCE}${queryString}`),
  show: (id) => http.get(`${CAMPAIGN_RESOURCE}/${id}`),
  create: (formData) =>
    http.post(`${CAMPAIGN_RESOURCE}`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
  dispatch: (id) => http.post(`${CAMPAIGN_RESOURCE}/${id}/dispatch`),
};

/** Gera e baixa o modelo CSV (nome, telefone, cpf) no próprio cliente. */
export function downloadCampaignTemplate() {
  const csv = "nome,telefone,cpf\nJoão da Silva,5511999998888,12345678900\nMaria Souza,5511988887777,98765432100";
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "modelo-campanha.csv";
  a.click();
  URL.revokeObjectURL(url);
}

// Chaves clínicas (APA) — marcam o papel de cada campo no escore pré-anestésico
export const CLINICAL_KEYS = [
  { value: "", label: "— nenhuma —" },
  { value: "name", label: "Nome completo" },
  { value: "age", label: "Idade" },
  { value: "weight", label: "Peso (kg)" },
  { value: "height", label: "Altura (m)" },
  { value: "cpf", label: "CPF" },
  { value: "phone", label: "Telefone" },
  { value: "sex", label: "Sexo" },
  { value: "pregnancy", label: "Gravidez" },
  { value: "cardiac", label: "Condições cardíacas (pesos)" },
  { value: "pulmonary", label: "Condições pulmonares (pesos)" },
  { value: "endocrine", label: "Condições endócrinas (pesos)" },
  { value: "diabetes", label: "Diabetes (sim/não)" },
  { value: "diabetes_insulin", label: "Diabetes insulino-dependente" },
  { value: "others", label: "Outras condições (pesos)" },
  { value: "smoking", label: "Tabagismo" },
  { value: "alcohol", label: "Etilismo" },
  { value: "drugs", label: "Outras drogas" },
  { value: "medications", label: "Medicamentos (suspensão)" },
  { value: "anesthesia_feeling", label: "Sentimento sobre anestesia" },
  { value: "consult_preference", label: "Preferência de consulta" },
];

// Quais clinicalKeys usam peso de risco por opção
export const CLINICAL_KEYS_WITH_SCORE = ["cardiac", "pulmonary", "endocrine", "others"];

// Nacionalidades (países) do campo CPF quando marcado "Estrangeiro".
// Ajuste livre — é só dados. value = label = nome em pt-BR.
export const NATIONALITIES = [
  "Afeganistão", "África do Sul", "Alemanha", "Angola", "Arábia Saudita", "Argentina",
  "Austrália", "Áustria", "Bélgica", "Bolívia", "Brasil", "Bulgária", "Canadá", "Chile",
  "China", "Colômbia", "Coreia do Sul", "Costa Rica", "Cuba", "Dinamarca", "Egito",
  "Emirados Árabes Unidos", "Equador", "Espanha", "Estados Unidos", "Filipinas", "Finlândia",
  "França", "Grécia", "Guatemala", "Haiti", "Holanda (Países Baixos)", "Honduras", "Hungria",
  "Índia", "Indonésia", "Irlanda", "Israel", "Itália", "Japão", "Líbano", "México", "Nigéria",
  "Noruega", "Nova Zelândia", "Panamá", "Paraguai", "Peru", "Polônia", "Portugal", "Reino Unido",
  "República Dominicana", "Rússia", "Suécia", "Suíça", "Tailândia", "Turquia", "Ucrânia",
  "Uruguai", "Venezuela", "Outra",
].map((n) => ({ value: n, label: n }));

// Fontes de variáveis disponíveis para mapear num template de WhatsApp
export const WHATSAPP_VAR_SOURCES = [
  { value: "first_name", label: "Primeiro nome do contato" },
  { value: "contact_name", label: "Nome completo do contato" },
  { value: "form_link", label: "Link do formulário" },
  { value: "form_title", label: "Título do formulário" },
  { value: "contact_email", label: "E-mail do contato" },
  { value: "custom", label: "Texto fixo" },
];
